import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Loader2, FileText, Download, RefreshCw, Wallet, Receipt } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusLabel = (status) => {
  if (status === 'paid') return 'Pagada';
  if (status === 'overdue') return 'Vencida';
  return 'Pendiente';
};

const statusClass = (status) => {
  if (status === 'paid') return 'bg-green-100 text-green-700';
  if (status === 'overdue') return 'bg-red-100 text-red-700';
  return 'bg-yellow-100 text-yellow-700';
};

const ClientBillingTab = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [permissions, setPermissions] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('session_token')}`,
  });

  const loadBillingData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, invoicesRes, methodsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/client-invoicing/billing/summary`, { headers: authHeaders() }),
        fetch(`${BACKEND_URL}/api/client-invoicing/billing/invoices`, { headers: authHeaders() }),
        fetch(`${BACKEND_URL}/api/client-invoicing/payment-methods`, { headers: authHeaders() }),
      ]);

      if (summaryRes.status === 403) {
        toast.error('No tienes permiso para ver facturación');
        return;
      }

      if (summaryRes.ok) {
        setSummary(await summaryRes.json());
      }
      if (invoicesRes.ok) {
        const data = await invoicesRes.json();
        setInvoices(data.invoices || []);
      }
      if (methodsRes.ok) {
        const data = await methodsRes.json();
        setPermissions(data.permissions || null);
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar facturación');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBillingData();
  }, [loadBillingData]);

  const downloadPdf = async (invoice) => {
    const id = invoice.holded_invoice_id || invoice.id;
    setDownloadingId(id);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/client-invoicing/billing/invoices/${id}/pdf`,
        { headers: authHeaders() }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'No se pudo descargar el PDF');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoice_number || id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || 'Error al descargar PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <Loader2 className="w-12 h-12 animate-spin mx-auto text-[#36e961]" />
        <p className="mt-4 text-gray-500">Cargando facturación...</p>
      </div>
    );
  }

  const fleet = summary?.fleet_save;
  const fleetPct =
    fleet?.enabled && fleet.credit_limit > 0
      ? Math.min(100, (fleet.used / fleet.credit_limit) * 100)
      : 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Facturación e Invoices</h2>
          <p className="text-gray-600 mt-1">
            Saldo del periodo, historial de facturas Holded y Fleet Save
          </p>
        </div>
        <Button variant="outline" onClick={loadBillingData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <Receipt className="w-4 h-4" /> Servicios sin facturar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900">
              {(summary?.unbilled_services ?? 0).toFixed(2)} €
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <FileText className="w-4 h-4" /> Suplidos sin facturar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900">
              {(summary?.unbilled_suplidos ?? 0).toFixed(2)} €
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Total acumulado</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-[#36e961]">
              {(summary?.unbilled_total ?? 0).toFixed(2)} €
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {summary?.pending_orders_count ?? 0} servicios pendientes
            </p>
          </CardContent>
        </Card>
      </div>

      {fleet?.enabled && fleet.credit_limit > 0 && (
        <Card className="border-[#36e961]/30 bg-gradient-to-r from-green-50 to-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Wallet className="w-5 h-5 text-[#36e961]" />
              Fleet Save
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-sm">
              <span>Consumido: {fleet.used.toFixed(2)} €</span>
              <span>Límite: {fleet.credit_limit.toFixed(2)} €</span>
            </div>
            <Progress value={fleetPct} className="h-3" />
            <p className="text-sm text-gray-600">
              Disponible: <strong>{fleet.available.toFixed(2)} €</strong>
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Historial de facturas</CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>No hay facturas sincronizadas todavía.</p>
              {permissions && !permissions.holded_contact_id && (
                <p className="text-xs mt-2">
                  Las facturas aparecerán aquí cuando Holded esté configurado y se emitan documentos.
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="py-2 pr-4">Nº Factura</th>
                    <th className="py-2 pr-4">Fecha</th>
                    <th className="py-2 pr-4">Tipo</th>
                    <th className="py-2 pr-4">Importe</th>
                    <th className="py-2 pr-4">Estado</th>
                    <th className="py-2">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id || inv.holded_invoice_id} className="border-b last:border-0">
                      <td className="py-3 pr-4 font-medium">{inv.invoice_number || '—'}</td>
                      <td className="py-3 pr-4">
                        {inv.issue_date
                          ? new Date(inv.issue_date).toLocaleDateString('es-ES')
                          : '—'}
                      </td>
                      <td className="py-3 pr-4 capitalize">
                        {inv.invoice_type === 'suplidos' ? 'Suplidos' : 'Servicios'}
                      </td>
                      <td className="py-3 pr-4">{(inv.total_amount ?? 0).toFixed(2)} €</td>
                      <td className="py-3 pr-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${statusClass(inv.payment_status)}`}
                        >
                          {statusLabel(inv.payment_status)}
                        </span>
                      </td>
                      <td className="py-3">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!inv.holded_invoice_id || downloadingId === inv.holded_invoice_id}
                          onClick={() => downloadPdf(inv)}
                        >
                          {downloadingId === inv.holded_invoice_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Download className="w-4 h-4 mr-1" />
                              PDF
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientBillingTab;
